import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import {
  defaultOpenSeasonIds,
  buildSeasonFilterOptions,
  seasonFilterPlaceholder,
  loadSeasons,
  resolveSeasonId,
} from "./seasonFilter";
import { loadFromSupabase, saveToSupabase, deleteFromSupabase } from "../supabaseDB";

/* ============================================================
   ConsignmentPricingRegistry — Client Pricing & POs · Tab 2
   ------------------------------------------------------------
   Tracks consignment-priced client POs (Twin River today) line
   by line. Differs from Fixed Pricing (Tab 1) in three key ways:

     1. Pricing is a RANGE (min / max), not a fixed point. TR
        gives Arturo a band per product per week and pays out
        a real number from somewhere inside that band after the
        sale lands.
     2. TR deducts a commission (10% default) from the sale price
        before paying GFI. The GF retain is therefore
        TR × (1 − commTR%).
     3. A "Diferencial" sits between what GFI keeps and what gets
        reported up to the exporter — that's the per-line spread
        Arturo decides he wants to retain on top of the GFI
        commission. The exporter sees:
                Grower Price = GF retain − Diferencial

   For projection BEFORE the sale, the table renders Estimated
   profit using the midpoint of the [min, max] band. Once
   `priceRealTR` is captured (after-sale), Real profit replaces
   Estimated in the bottom summary while both stay visible in
   the table for comparison.

   Storage: localStorage `gfi-consignment-pos`
   Window globals:
     - window.__gfiGetMatchingConsignmentPOs(date, clientId)
         Returns up to 5 PO lines within ±7 days for the
         Invoice form to suggest at invoice-create time.
     - window.__gfiLinkInvoiceToConsignmentPo(poLineId, invoiceId, priceRealTR)
         Stamps linkedInvoiceId + priceRealTR onto the PO line
         so the Real-profit columns turn on.
   ============================================================ */

const T = {
  en: {
    title: "Consignment Pricing & POs", subtitle: "Per-line consignment POs with range pricing and live profit projection",
    add: "+ New PO Line", edit: "Edit PO line", view: "View PO line",
    search: "Search by PO / PU / Product / Client...", noResults: "No PO lines match the filters.",
    /* Cards */
    cardLines: "PO Lines", cardCases: "Total Cases",
    cardEstRevenue: "Est. Revenue (TR mid)", cardEstProfit: "Est. Profit",
    cardRealProfit: "Real Profit", cardSettled: "Settled",
    /* Columns */
    colDate: "Departure\nDate", colWeek: "Wk", colDelivery: "Loading/\nDelivery Date", colClient: "Client",
    colPO: "PO #", colPU: "PU #",
    colProduct: "Product", colCat: "CAT",
    colPallets: "Pallets", colCases: "Cases",
    /* Range pricing */
    colMinTR: "Min\nTR", colMaxTR: "Max\nTR", colCommTR: "Comm\nTR %",
    colMinGF: "Min\nGF", colMaxGF: "Max\nGF",
    colDifferential: "Diff.",
    colRealDifferential: "Real\nDiff.",
    colMinGrower: "Min\nGrower", colMaxGrower: "Max\nGrower",
    colCommGFI: "Comm\nGFI %",
    colRealCommTR: "Real\nComm TR",
    colRealCommGFI: "Real\nComm GFI",
    /* After-sale */
    colPriceRealTR: "Real\nTR", colReturnRealGF: "Real\nGF", colPriceRealGrower: "Real\nGrower",
    colProfitDiff: "Diff\nProfit", colProfitComm: "Comm\nProfit",
    colProfitTotal: "Real\nProfit", colEstTotal: "Est.\nProfit",
    colSaleStatus: "Sale\nStatus",
    /* Form sections */
    secHeader: "PO Header", secProduct: "Product", secQty: "Quantities",
    secTRRange: "Twin River Range Pricing", secSpread: "GFI Spread (Differential + Commission)",
    secReal: "After-Sale (Real Numbers)", secMeta: "Status & Notes",
    /* Form fields */
    date: "Departure Date", deliveryDate: "Loading/Delivery Date",
    departureHint: "Auto-calculated as 2 days before delivery — editable",
    client: "Client", selectClient: "Select client...",
    poNumber: "PO #", puNumber: "PU #",
    product: "Product", selectProduct: "Select product...",
    category: "Category",
    pallets: "Pallets", cases: "Cases",
    minTR: "Min TR Price", maxTR: "Max TR Price",
    commTR: "TR Commission %", commTRSub: "TR deducts this from the sale price (default 10%)",
    realCommTR: "Real TR Commission %", realCommTRSub: "Actual TR commission after sale. If empty, falls back to estimated. Use 0 if TR waived it.",
    minGF: "Min GF (retained)", maxGF: "Max GF (retained)",
    differential: "Differential ($/case)", differentialSub: "Extra GFI keeps before reporting to exporter (manual)",
    realDifferential: "Real Differential ($/case)", realDifferentialSub: "Actual differential after sale. If empty, falls back to estimated.",
    minGrower: "Min Grower Price", maxGrower: "Max Grower Price",
    commGFI: "GFI Commission %", commGFISub: "Default 5%",
    realCommGFI: "Real GFI Commission %", realCommGFISub: "Actual GFI commission after sale. If empty, falls back to estimated. Use 0 if you waived it.",
    advance: "Advance ($/case)", advanceSub: "TR pays $4/case weekly. Arturo decides how much to pass through.",
    priceRealTR: "Real TR Price (post-sale)", priceRealTRHint: "Captured after the sale lands. Triggers Real Profit columns.",
    priceEstTR: "Estimated TR Price", priceEstTRHint: "Your pre-sale estimate. If set, estimated commission uses this instead of the midpoint.",
    returnRealGF: "Real GF Return", priceRealGrower: "Real Grower Price",
    saleStatus: "Sale Status", selectStatus: "Select status...",
    linkedInvoice: "Linked Invoice", notes: "Notes", notesPlaceholder: "Internal notes...",
    /* Profit summary */
    estimatedSummary: "Estimated Profit Summary (midpoint)",
    realSummary: "Real Profit Summary",
    profitDiffDesc: "Differential × cases",
    profitCommDesc: "Grower price × Comm GFI% × cases",
    profitTotalDesc: "Total profit",
    midpointHint: "Estimated uses the midpoint of [Min, Max] range",
    /* Status */
    statusPending: "Pending Sale", statusDraft: "Draft", statusSent: "Sent", statusPaid: "Paid in Full", statusInvoiced: "Invoiced",
    statusGoodDelivery: "Good Delivery", statusProtection: "Protection", statusPAS: "PAS",
    statusSettled: "Settled", statusDumped: "Dumped", statusDonated: "Donated", statusSamples: "Samples", statusTransfer: "Transfer",
    /* Filters */
    allClients: "All clients", allStatuses: "All statuses",
    /* Actions */
    save: "Save", saveAndNew: "Save & New", cancel: "Cancel", del: "Delete",
    editBtn: "Edit", closeBtn: "Close",
    confirmDel: "Delete this PO line? This cannot be undone.",
    confirmExit: "Unsaved changes. Exit anyway?", yesExit: "Yes, exit", noExit: "Keep editing",
    /* Validation */
    noDate: "Date is required.", noPO: "PO # is required.",
    noProduct: "Product is required.", noCases: "Cases is required.",
    noMinTR: "Min TR price is required.", noMaxTR: "Max TR price is required.",
    /* Toasts */
    saved: "PO line saved", deleted: "PO line deleted",
    clearForm: "Clear Form", confirmClear: "Clear all form fields?",
    weekShort: "W",
  },
  es: {
    title: "Precios en Consignación y POs", subtitle: "POs de cliente con precios en rango y proyección de ganancia en vivo",
    add: "+ Nueva Línea de PO", edit: "Editar línea de PO", view: "Ver línea de PO",
    search: "Buscar por PO / PU / Producto / Cliente...", noResults: "Ninguna línea coincide con los filtros.",
    cardLines: "Líneas PO", cardCases: "Cajas Totales",
    cardEstRevenue: "Venta Est. (TR medio)", cardEstProfit: "Ganancia Estimada",
    cardRealProfit: "Ganancia Real", cardSettled: "Liquidado",
    colDate: "Fecha\nSalida", colWeek: "Sem", colDelivery: "Carga/\nEntrega", colClient: "Cliente",
    colPO: "PO #", colPU: "PU #",
    colProduct: "Producto", colCat: "CAT",
    colPallets: "Pallets", colCases: "Cajas",
    colMinTR: "Min\nTR", colMaxTR: "Max\nTR", colCommTR: "Com\nTR %",
    colMinGF: "Min\nGF", colMaxGF: "Max\nGF",
    colDifferential: "Difer.",
    colRealDifferential: "Difer.\nReal",
    colMinGrower: "Min\nExp.", colMaxGrower: "Max\nExp.",
    colCommGFI: "Com\nGFI %",
    colRealCommTR: "Com Real\nTR",
    colRealCommGFI: "Com Real\nGFI",
    colPriceRealTR: "Real\nTR", colReturnRealGF: "Real\nGF", colPriceRealGrower: "Real\nExp.",
    colProfitDiff: "Gan.\nDifer.", colProfitComm: "Gan.\nCom.",
    colProfitTotal: "Gan.\nReal", colEstTotal: "Gan.\nEst.",
    colSaleStatus: "Status\nVenta",
    secHeader: "Encabezado PO", secProduct: "Producto", secQty: "Cantidades",
    secTRRange: "Precios en Rango Twin River", secSpread: "Spread GFI (Diferencial + Comisión)",
    secReal: "Post-Venta (Números Reales)", secMeta: "Estado y Notas",
    date: "Fecha de Salida", deliveryDate: "Fecha de Carga/Entrega",
    departureHint: "Se auto-calcula 2 días antes de la entrega — editable",
    client: "Cliente", selectClient: "Seleccionar cliente...",
    poNumber: "PO #", puNumber: "PU #",
    product: "Producto", selectProduct: "Seleccionar producto...",
    category: "Categoría",
    pallets: "Pallets", cases: "Cajas",
    minTR: "Precio Min TR", maxTR: "Precio Max TR",
    commTR: "Comisión TR %", commTRSub: "TR descuenta este % del precio de venta (default 10%)",
    realCommTR: "Comisión Real TR %", realCommTRSub: "Comisión TR real post-venta. Si se deja vacío, usa el estimado. Usa 0 si TR no cobró.",
    minGF: "Min GF (retenido)", maxGF: "Max GF (retenido)",
    differential: "Diferencial ($/caja)", differentialSub: "Extra que GFI retiene antes de reportar al exportador (manual)",
    realDifferential: "Diferencial Real ($/caja)", realDifferentialSub: "Diferencial real post-venta. Si se deja vacío, usa el estimado.",
    minGrower: "Precio Min Exportador", maxGrower: "Precio Max Exportador",
    commGFI: "Comisión GFI %", commGFISub: "Default 5%",
    realCommGFI: "Comisión Real GFI %", realCommGFISub: "Comisión GFI real post-venta. Si se deja vacío, usa el estimado. Usa 0 si la removiste.",
    advance: "Anticipo ($/caja)", advanceSub: "TR paga $4/caja semanal. Arturo decide cuánto pasar al exportador.",
    priceRealTR: "Precio Real TR (post-venta)", priceRealTRHint: "Se captura después de la venta. Activa las columnas de Ganancia Real.",
    priceEstTR: "Precio Estimado TR", priceEstTRHint: "Tu estimación pre-venta. Si se llena, la comisión estimada usa este precio en lugar del midpoint.",
    returnRealGF: "Retorno Real GF", priceRealGrower: "Precio Real Exportador",
    saleStatus: "Status de Venta", selectStatus: "Seleccionar estado...",
    linkedInvoice: "Factura Vinculada", notes: "Notas", notesPlaceholder: "Notas internas...",
    estimatedSummary: "Resumen de Ganancia Estimada (midpoint)",
    realSummary: "Resumen de Ganancia Real",
    profitDiffDesc: "Diferencial × cajas",
    profitCommDesc: "Precio exportador × Com GFI% × cajas",
    profitTotalDesc: "Ganancia total",
    midpointHint: "La estimada usa el midpoint del rango [Min, Max]",
    statusPending: "Pendiente Venta", statusDraft: "Borrador", statusSent: "Enviada", statusPaid: "Pago Completo", statusInvoiced: "Facturado",
    statusGoodDelivery: "Good Delivery", statusProtection: "Protección", statusPAS: "PAS",
    statusSettled: "Liquidado", statusDumped: "Dumped", statusDonated: "Donated", statusSamples: "Samples", statusTransfer: "Transferencia",
    allClients: "Todos los clientes", allStatuses: "Todos los estados",
    save: "Guardar", saveAndNew: "Guardar y Nuevo", cancel: "Cancelar", del: "Eliminar",
    editBtn: "Editar", closeBtn: "Cerrar",
    confirmDel: "¿Eliminar esta línea de PO? No se puede deshacer.",
    confirmExit: "Cambios sin guardar. ¿Salir de todos modos?", yesExit: "Sí, salir", noExit: "Seguir editando",
    noDate: "La fecha es requerida.", noPO: "El PO # es requerido.",
    noProduct: "El producto es requerido.", noCases: "Las cajas son requeridas.",
    noMinTR: "El precio Min TR es requerido.", noMaxTR: "El precio Max TR es requerido.",
    saved: "Línea de PO guardada", deleted: "Línea de PO eliminada",
    clearForm: "Limpiar Formulario", confirmClear: "¿Limpiar todos los campos?",
    weekShort: "Sem",
  },
};

/* ---------- Constants ---------- */
const STORAGE_KEY = "gfi-consignment-pos";
const DEFAULT_COMM_TR = 10;
const DEFAULT_COMM_GFI = 5;
const SALE_STATUSES = [
  "pending", "draft", "sent", "invoiced", "paid",
  "goodDelivery", "protection", "pas",
  "settled", "dumped", "donated", "samples", "transfer",
];
const STATUS_COLORS = {
  pending:      { bg: "rgba(156,163,175,0.15)", text: "#6B7280", border: "rgba(156,163,175,0.3)" },
  /* Invoice-side statuses mirrored from the Invoices module so a
     PO linked to an invoice shows the invoice's actual state. */
  draft:        { bg: "rgba(107,114,128,0.12)", text: "#4B5563", border: "rgba(107,114,128,0.3)" },
  sent:         { bg: "rgba(245,158,11,0.15)",  text: "#B45309", border: "rgba(245,158,11,0.3)" },
  paid:         { bg: "rgba(16,185,129,0.15)",  text: "#047857", border: "rgba(16,185,129,0.3)" },
  invoiced:     { bg: "rgba(59,130,246,0.15)",  text: "#2563EB", border: "rgba(59,130,246,0.3)" },
  goodDelivery: { bg: "rgba(34,197,94,0.15)",   text: "#15803D", border: "rgba(34,197,94,0.3)" },
  protection:   { bg: "rgba(245,158,11,0.15)",  text: "#B45309", border: "rgba(245,158,11,0.3)" },
  pas:          { bg: "rgba(217,70,239,0.15)",  text: "#A21CAF", border: "rgba(217,70,239,0.3)" },
  settled:      { bg: "rgba(249,115,22,0.15)",  text: "#C2410C", border: "rgba(249,115,22,0.35)" },
  dumped:       { bg: "rgba(220,38,38,0.15)",   text: "#B91C1C", border: "rgba(220,38,38,0.3)" },
  donated:      { bg: "rgba(20,184,166,0.15)",  text: "#0F766E", border: "rgba(20,184,166,0.3)" },
  samples:      { bg: "rgba(168,85,247,0.15)",  text: "#7E22CE", border: "rgba(168,85,247,0.3)" },
  transfer:     { bg: "rgba(59,130,246,0.15)",  text: "#2563EB", border: "rgba(59,130,246,0.3)" },
};

const EMPTY = {
  id: "", createdAt: "", updatedAt: "",
  date: "", week: "", deliveryDate: "", shipDate: "",
  clientId: "", clientName: "",
  poNumber: "", puNumber: "",
  productId: "", productBase: "", productName: "", productDescription: "", category: "",
  pallets: "", cases: "",
  /* TR-side range */
  minTR: "", maxTR: "",
  commTR: DEFAULT_COMM_TR,
  realCommTR: "",
  /* GFI spread */
  differential: "",
  realDifferential: "",
  commGFI: DEFAULT_COMM_GFI,
  realCommGFI: "",
  advance: "",
  /* After-sale */
  priceRealTR: "",
  priceEstTR: "",
  saleStatus: "pending",
  linkedInvoiceId: "",
  seasonId: "",
  notes: "",
};

const inp = { padding: "7px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
const errS = { borderColor: "#DC2626", background: "#FFF8E1" };
const readOnlyInp = { ...inp, background: "var(--color-bg)", color: "var(--color-text-muted)", borderStyle: "dashed" };

/* ---------- Storage ---------- */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }
function getPOs() { return loadLS(STORAGE_KEY, []); }
function getClients() { return loadLS("gfi-clients", []); }
function getProducts() { return loadLS("gfi-products", []); }
function getInvoices() { return loadLS("gfi-invoices", []); }

/* Same ISO-week impl pattern used across modules — duplicated per
   the "intentionally not shared" rule in CLAUDE.md. */
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  var target = new Date(d.valueOf());
  var day = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - day + 3);
  var firstThu = new Date(target.getFullYear(), 0, 4);
  var diff = target - firstThu;
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
}

function buildProductLabel(p) {
  if (!p) return "";
  var bits = [];
  if (p.product) bits.push(p.product);
  if (p.unitsCase && p.size) bits.push(p.unitsCase + "/" + p.size);
  else if (p.size) bits.push(p.size);
  var cat = String(p.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV";
  bits.push(cat);
  if (p.labelInitials) bits.push(p.labelInitials + " Label");
  return bits.join(" ");
}

function fmtMoney(n) {
  var v = parseFloat(n);
  if (isNaN(v)) return "—";
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMoneyShort(n) {
  var v = parseFloat(n);
  if (isNaN(v) || v === 0) return "—";
  /* Thousand separators applied app-wide for consistency. */
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* Pure pricing math — all derived fields computed from the 6
   inputs (minTR, maxTR, commTR, differential, commGFI, priceRealTR).
   `cases` is needed for the absolute-dollar profit numbers.

   Range derivations:
     minGF      = minTR × (1 − commTR%)
     maxGF      = maxTR × (1 − commTR%)
     minGrower  = minGF − differential
     maxGrower  = maxGF − differential
   Midpoint (used for projection):
     midTR      = (minTR + maxTR) / 2
     midGF      = midTR × (1 − commTR%)
     midGrower  = midGF − differential
   Real (post-sale):
     realGF     = priceRealTR × (1 − commTR%)
     realGrower = realGF − differential
   Profit components:
     profitDiff = differential × cases
     profitComm = (real or mid) grower × commGFI% × cases
     profitTotal = profitDiff + profitComm                          */
function calcConsignment(line) {
  var cases   = parseInt(line.cases, 10) || 0;
  var minTR   = parseFloat(line.minTR) || 0;
  var maxTR   = parseFloat(line.maxTR) || 0;
  var commTR  = parseFloat(line.commTR); if (isNaN(commTR)) commTR = DEFAULT_COMM_TR;
  var hasRealCommTR = line.realCommTR !== "" && line.realCommTR != null && !isNaN(parseFloat(line.realCommTR));
  var realCommTR = hasRealCommTR ? parseFloat(line.realCommTR) : commTR;
  var diff    = parseFloat(line.differential) || 0;
  var hasRealDiff = line.realDifferential !== "" && line.realDifferential != null && !isNaN(parseFloat(line.realDifferential));
  var realDiff = hasRealDiff ? parseFloat(line.realDifferential) : diff;
  var commGFI = parseFloat(line.commGFI); if (isNaN(commGFI)) commGFI = DEFAULT_COMM_GFI;
  var hasRealCommGFI = line.realCommGFI !== "" && line.realCommGFI != null && !isNaN(parseFloat(line.realCommGFI));
  var realCommGFI = hasRealCommGFI ? parseFloat(line.realCommGFI) : commGFI;
  var trMult  = 1 - (commTR / 100);
  var trMultReal = 1 - (realCommTR / 100);

  var minGF = minTR * trMult;
  var maxGF = maxTR * trMult;
  var minGrower = minGF - diff;
  var maxGrower = maxGF - diff;

  /* Estimated TR price: use the user's pre-sale estimate when provided,
     otherwise fall back to the midpoint of the [min, max] band. This
     drives the estimated commission. */
  var hasEst = line.priceEstTR !== "" && line.priceEstTR != null && !isNaN(parseFloat(line.priceEstTR));
  var midTR = hasEst ? parseFloat(line.priceEstTR) : (minTR + maxTR) / 2;
  var midGF = midTR * trMult;
  var midGrower = midGF - diff;

  var hasReal = line.priceRealTR !== "" && line.priceRealTR != null && !isNaN(parseFloat(line.priceRealTR));
  var realTR = hasReal ? parseFloat(line.priceRealTR) : 0;
  var realGF = hasReal ? realTR * trMultReal : 0;
  var realGrower = hasReal ? realGF - realDiff : 0;

  var profitDiff = diff * cases;
  var realProfitDiff = realDiff * cases;
  var estProfitComm = midGrower * (commGFI / 100) * cases;
  var estTotal = profitDiff + estProfitComm;

  var realProfitComm = hasReal ? realGrower * (realCommGFI / 100) * cases : 0;
  var realTotal = hasReal ? (realProfitDiff + realProfitComm) : 0;

  return {
    minGF: minGF, maxGF: maxGF, minGrower: minGrower, maxGrower: maxGrower,
    midTR: midTR, midGF: midGF, midGrower: midGrower,
    hasReal: hasReal, realTR: realTR, realGF: realGF, realGrower: realGrower,
    realDiff: realDiff, hasRealDiff: hasRealDiff,
    realCommTR: realCommTR, hasRealCommTR: hasRealCommTR,
    realCommGFI: realCommGFI, hasRealCommGFI: hasRealCommGFI,
    profitDiff: profitDiff, realProfitDiff: realProfitDiff,
    estProfitComm: estProfitComm, estTotal: estTotal,
    realProfitComm: realProfitComm, realTotal: realTotal,
  };
}

/* ---------- Inline UI primitives ---------- */
function Field({ label, required, error, sub, children, full }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, gridColumn: full ? "1 / -1" : "auto" }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}{required && <span style={{ color: "#DC2626", fontSize: 10 }}>*</span>}
      </label>
      {children}
      {sub && <span style={{ fontSize: 10, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}
      {error && <span style={{ fontSize: 10, color: "#DC2626", fontWeight: 600 }}>{error}</span>}
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid var(--color-border)", paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}
/* 2026-10-21, Arturo: "matea toda la app" — same rectangular/3D-bevel
   recipe as ShipmentRegistry.js's TEXAS_QC_BEVEL, applied here same
   as FixedPricingRegistry.js's own StatusBadge. */
var STATUS_BEVEL = "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.12)";
function StatusBadge({ status, t }) {
  var c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  var label = t["status" + (status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending")] || status;
  return <span style={{ padding: "3px 8px", borderRadius: 0, fontSize: 10, fontWeight: 700, background: c.bg, color: c.text, border: "1px solid " + c.border, boxShadow: STATUS_BEVEL, whiteSpace: "nowrap", display: "inline-block" }}>{label}</span>;
}
function Toast({ msg, onDismiss }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    if (!msg) return;
    setFading(false);
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(onDismiss, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
    /* onDismiss deliberately excluded — inline arrow from parent
       creates a new identity per render, resetting the timer. The
       setToast setter captured inside onDismiss is stable, so the
       closure remains safe. */
  }, [msg]);
  if (!msg) return null;
  return createPortal(<div style={{ position: "fixed", bottom: 24, right: 24, padding: "10px 18px", background: "var(--color-accent)", color: "var(--color-accent-text)", borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 999999, animation: fading ? "fadeOutCorner 0.3s ease forwards" : undefined }}>{msg}</div>, document.body);
}
function ProfitCell({ title, amount, desc, emphasis }) {
  var positive = amount > 0; var negative = amount < 0;
  var color = emphasis ? "var(--color-accent)" : (positive ? "#047857" : negative ? "#B91C1C" : "var(--color-text-secondary)");
  return (
    <div style={{ padding: "10px 12px", borderRadius: 6, border: "1px solid " + (emphasis ? "var(--color-accent)" : "var(--color-border)"), background: emphasis ? "rgba(42,122,111,0.05)" : "var(--color-bg)" }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
      <div style={{ fontSize: emphasis ? 18 : 14, fontWeight: emphasis ? 800 : 700, fontFamily: "'JetBrains Mono', monospace", color: color, marginTop: 4 }}>{fmtMoney(amount)}</div>
      <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>{desc}</div>
    </div>
  );
}

/* ======================== FORM MODAL ======================== */
function PoLineModal({ line, t, lang, onSave, onDelete, onClose }) {
  var isNew = !line || !line.id;
  var [mode, setMode] = useState(isNew ? "edit" : "view");
  var [f, setF] = useState(function () { return line ? { ...EMPTY, ...line } : { ...EMPTY }; });
  var [errors, setErrors] = useState({});
  var [dirty, setDirty] = useState(false);
  var [showExit, setShowExit] = useState(false);
  var origRef = useMemo(function () { return JSON.stringify(line ? { ...EMPTY, ...line } : { ...EMPTY }); }, [line]);

  var clients = useMemo(function () { return getClients(); }, []);
  var products = useMemo(function () { return getProducts(); }, []);
  var seasons = useMemo(function () { return loadSeasons(); }, []);

  var set = function (k, v) { setF(function (prev) { return { ...prev, [k]: v }; }); setDirty(true); setErrors(function (p) { return { ...p, [k]: false }; }); };
  /* Delivery Date → Shipping Date auto-fill. Per business rule the
     truck leaves MX 2 days before the warehouse receives. Always
     recomputed when delivery changes; user can still override the
     departure date manually after. Week is derived from the delivery
     date itself (not from the departure date); season tracks the
     derived departure date. */
  var handleDeliveryDateChange = function (v) {
    setF(function (prev) {
      var next = { ...prev, deliveryDate: v, week: v ? getISOWeek(v) : "" };
      if (v) {
        var d = new Date(v + "T00:00:00");
        if (!isNaN(d.getTime())) {
          d.setDate(d.getDate() - 2);
          var yyyy = d.getFullYear();
          var mm = String(d.getMonth() + 1).padStart(2, "0");
          var dd = String(d.getDate()).padStart(2, "0");
          var depISO = yyyy + "-" + mm + "-" + dd;
          next.date = depISO;
          var sid = resolveSeasonId(seasons, depISO, prev.seasonId);
          if (sid) next.seasonId = sid;
        }
      }
      return next;
    });
    setDirty(true);
    setErrors(function (p) { return { ...p, deliveryDate: false }; });
  };
  /* Departure date change leaves week alone — week follows delivery. */
  var handleDateChange = function (v) {
    setF(function (prev) {
      var next = { ...prev, date: v };
      var sid = resolveSeasonId(seasons, v, prev.seasonId);
      if (sid) next.seasonId = sid;
      return next;
    });
    setDirty(true);
    setErrors(function (p) { return { ...p, date: false }; });
  };
  var handleProductChange = function (productId) {
    var prod = products.find(function (p) { return String(p.id) === String(productId); });
    if (!prod) { set("productId", productId); return; }
    var cat = String(prod.category || "").toLowerCase().indexOf("org") >= 0 ? "OG" : "CV";
    setF(function (prev) {
      return {
        ...prev,
        productId: productId,
        productBase: prod.product || "",
        productName: prod.product || "",
        productDescription: buildProductLabel(prod),
        category: cat,
      };
    });
    setDirty(true);
    setErrors(function (p) { return { ...p, productId: false }; });
  };
  var handleClientChange = function (clientId) {
    var cl = clients.find(function (c) { return String(c.id) === String(clientId); });
    setF(function (prev) { return { ...prev, clientId: clientId, clientName: cl ? (cl.shortName || cl.fullName || cl.name || "") : "" }; });
    setDirty(true);
  };

  var validate = function () {
    var errs = {};
    if (!f.date) errs.date = t.noDate;
    if (!String(f.poNumber || "").trim()) errs.poNumber = t.noPO;
    if (!f.productId) errs.productId = t.noProduct;
    if (!parseInt(f.cases, 10)) errs.cases = t.noCases;
    if (!String(f.minTR || "").trim()) errs.minTR = t.noMinTR;
    if (!String(f.maxTR || "").trim()) errs.maxTR = t.noMaxTR;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  var doSave = function (keepOpen) {
    if (!validate()) return;
    var saved = {
      ...f,
      id: f.id || ("CPO-" + Date.now() + "-" + Math.floor(Math.random() * 1000)),
      createdAt: f.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    onSave(saved);
    setDirty(false);
    if (keepOpen) {
      /* Save & New carries the trip-level scaffold forward so the
         user can rip through multiple product lines for the same PO
         without re-typing date / client / PO / commissions. */
      var carry = {
        ...EMPTY,
        date: saved.date, week: saved.week,
        deliveryDate: saved.deliveryDate,
        clientId: saved.clientId, clientName: saved.clientName,
        poNumber: saved.poNumber, puNumber: saved.puNumber,
        seasonId: saved.seasonId,
        commTR: saved.commTR, commGFI: saved.commGFI,
        advance: saved.advance,
      };
      setF(carry);
      setErrors({});
      setMode("edit");
    } else {
      setMode("view");
    }
  };
  var tryClose = function () {
    if (mode === "edit" && dirty && JSON.stringify(f) !== origRef) { setShowExit(true); return; }
    onClose();
  };
  var doDelete = function () {
    if (!window.confirm(t.confirmDel)) return;
    onDelete(f.id);
  };
  var doClear = function () {
    if (!window.confirm(t.confirmClear)) return;
    setF(function (prev) { return { ...EMPTY, id: prev.id, createdAt: prev.createdAt }; });
    setErrors({});
    setDirty(true);
  };

  var locked = mode === "view";
  var c = calcConsignment(f);
  var clientOpts = clients.filter(function (cl) { return cl.isActive !== false; }).map(function (cl) { return { value: String(cl.id), label: cl.shortName || cl.fullName || cl.name || "" }; });
  var productOpts = products.map(function (p) { return { value: String(p.id), label: buildProductLabel(p) }; });
  var statusOpts = SALE_STATUSES.map(function (s) { return { value: s, label: t["status" + s.charAt(0).toUpperCase() + s.slice(1)] || s }; });

  return (
    <DraggableModal title={(isNew ? t.add : (locked ? t.view : t.edit)) + (f.poNumber ? "  ·  " + f.poNumber : "")} onClose={tryClose} initialWidth={Math.min(1100, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(840, window.innerHeight - 60)}>
      {showExit && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
          <div style={{ background: "var(--color-bg-card-solid)", borderRadius: 8, padding: 20, maxWidth: 380, border: "1px solid var(--color-border)" }}>
            <div style={{ fontSize: 13, color: "var(--color-text)", marginBottom: 16 }}>{t.confirmExit}</div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={function () { setShowExit(false); }} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12 }}>{t.noExit}</button>
              <button onClick={function () { setShowExit(false); onClose(); }} style={{ padding: "6px 14px", borderRadius: 5, border: "none", background: "#DC2626", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.yesExit}</button>
            </div>
          </div>
        </div>
      )}

      {/* Top action strip */}
      <div style={{ padding: "10px 18px", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <StatusBadge status={f.saleStatus || "pending"} t={t} />
          {f.week && <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 600, background: "var(--color-bg-card)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)" }}>{t.weekShort + f.week}</span>}
          {f.linkedInvoiceId && <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.linkedInvoice}: <strong style={{ color: "var(--color-accent)" }}>{f.linkedInvoiceId}</strong></span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {!isNew && locked && <button onClick={doDelete} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "transparent", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.del}</button>}
          {locked && <button onClick={function () { setMode("edit"); }} style={{ padding: "6px 14px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.editBtn}</button>}
          {!locked && <button onClick={doClear} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🧹 "}{t.clearForm}</button>}
          {!locked && <button onClick={function () { doSave(true); }} style={{ padding: "6px 14px", borderRadius: 5, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.saveAndNew}</button>}
          {!locked && <button onClick={function () { doSave(false); }} style={{ padding: "6px 16px", borderRadius: 5, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.save}</button>}
          <button onClick={tryClose} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.closeBtn}</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px" }}>
        <Section title={t.secHeader}>
          {/* Date row — Loading/Delivery (primary capture) →
              Departure (auto-fill, editable) → Week. Both dates
              live in the same row so the cascade is obvious. */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.5fr 1fr 1fr 0.8fr", gap: 10 }}>
            <Field label={t.deliveryDate} required error={errors.deliveryDate}>
              <input type="date" value={f.deliveryDate || ""} onChange={function (e) { handleDeliveryDateChange(e.target.value); }} disabled={locked} style={{ ...inp, ...(errors.deliveryDate ? errS : {}) }} />
            </Field>
            <Field label={t.date} sub={t.departureHint}>
              <input type="date" value={f.date || ""} onChange={function (e) { handleDateChange(e.target.value); }} disabled={locked} style={{ ...inp, ...(errors.date ? errS : {}) }} />
            </Field>
            <Field label={t.weekShort}>
              <input value={f.week ? "W" + f.week : ""} readOnly style={{ ...inp, background: "var(--color-bg)", color: "var(--color-text-muted)", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }} />
            </Field>
            <Field label={t.client}>
              <SearchSelect value={f.clientId} onChange={handleClientChange} placeholder={t.selectClient} options={clientOpts} disabled={locked} />
            </Field>
            <Field label={t.poNumber} required error={errors.poNumber}>
              <input value={f.poNumber || ""} onChange={function (e) { set("poNumber", e.target.value); }} disabled={locked} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...(errors.poNumber ? errS : {}) }} />
            </Field>
            <Field label={t.puNumber}>
              <input value={f.puNumber || ""} onChange={function (e) { set("puNumber", e.target.value); }} disabled={locked} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
          </div>
        </Section>

        <Section title={t.secProduct}>
          <div style={{ display: "grid", gridTemplateColumns: "2.4fr 0.5fr 0.6fr 0.6fr", gap: 10 }}>
            <Field label={t.product} required error={errors.productId}>
              <SearchSelect value={f.productId} onChange={handleProductChange} placeholder={t.selectProduct} options={productOpts} disabled={locked} />
            </Field>
            <Field label={t.category}>
              <input value={f.category || ""} readOnly style={{ ...inp, background: "var(--color-bg)", color: "var(--color-text-muted)", textAlign: "center", fontWeight: 700 }} />
            </Field>
            <Field label={t.pallets}>
              <input type="number" min="0" value={f.pallets || ""} onChange={function (e) { set("pallets", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
            <Field label={t.cases} required error={errors.cases}>
              <input type="number" min="0" value={f.cases || ""} onChange={function (e) { set("cases", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...(errors.cases ? errS : {}) }} />
            </Field>
          </div>
        </Section>

        <Section title={t.secTRRange}>
          {/* Captured (Min / Max / Comm TR) + computed (Min/Max GF) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.8fr 1fr 1fr", gap: 10 }}>
            <Field label={t.minTR} required error={errors.minTR}>
              <input type="number" min="0" step="0.01" value={f.minTR || ""} onChange={function (e) { set("minTR", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...(errors.minTR ? errS : {}) }} />
            </Field>
            <Field label={t.maxTR} required error={errors.maxTR}>
              <input type="number" min="0" step="0.01" value={f.maxTR || ""} onChange={function (e) { set("maxTR", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, ...(errors.maxTR ? errS : {}) }} />
            </Field>
            <Field label={t.commTR} sub={t.commTRSub}>
              <input type="number" min="0" step="0.01" value={f.commTR === "" || f.commTR == null ? "" : f.commTR} onChange={function (e) { set("commTR", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }} />
            </Field>
            <Field label={t.minGF}>
              <input value={c.minGF > 0 ? fmtMoneyShort(c.minGF) : "—"} readOnly style={{ ...readOnlyInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
            <Field label={t.maxGF}>
              <input value={c.maxGF > 0 ? fmtMoneyShort(c.maxGF) : "—"} readOnly style={{ ...readOnlyInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
          </div>
        </Section>

        <Section title={t.secSpread}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 0.7fr 0.8fr", gap: 10 }}>
            <Field label={t.differential} sub={t.differentialSub}>
              <input type="number" min="0" step="0.01" value={f.differential || ""} onChange={function (e) { set("differential", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }} />
            </Field>
            <Field label={t.minGrower}>
              <input value={c.minGrower !== 0 || c.minGF > 0 ? fmtMoneyShort(c.minGrower) : "—"} readOnly style={{ ...readOnlyInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
            <Field label={t.maxGrower}>
              <input value={c.maxGrower !== 0 || c.maxGF > 0 ? fmtMoneyShort(c.maxGrower) : "—"} readOnly style={{ ...readOnlyInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
            <Field label={t.commGFI} sub={t.commGFISub}>
              <input type="number" min="0" step="0.01" value={f.commGFI === "" || f.commGFI == null ? "" : f.commGFI} onChange={function (e) { set("commGFI", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }} />
            </Field>
            <Field label={t.advance} sub={t.advanceSub}>
              <input type="number" min="0" step="0.01" value={f.advance || ""} onChange={function (e) { set("advance", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
          </div>
        </Section>

        {/* Estimated profit summary — always visible. Uses midpoint. */}
        <Section title={t.estimatedSummary}>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 6, fontStyle: "italic" }}>{t.midpointHint}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
            <ProfitCell title={t.colProfitDiff} amount={c.profitDiff} desc={t.profitDiffDesc} />
            <ProfitCell title={t.colProfitComm} amount={c.estProfitComm} desc={t.profitCommDesc} />
            <ProfitCell title={t.colEstTotal} amount={c.estTotal} desc={t.profitTotalDesc} emphasis />
          </div>
        </Section>

        <Section title={t.secReal}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label={t.priceEstTR} sub={t.priceEstTRHint}>
              <input type="number" min="0" step="0.01" value={f.priceEstTR || ""} onChange={function (e) { set("priceEstTR", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, background: f.priceEstTR ? "rgba(59,130,246,0.06)" : inp.background }} />
            </Field>
            <Field label={t.priceRealTR} sub={t.priceRealTRHint}>
              <input type="number" min="0" step="0.01" value={f.priceRealTR || ""} onChange={function (e) { set("priceRealTR", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, background: f.priceRealTR ? "rgba(16,185,129,0.06)" : inp.background }} />
            </Field>
            <Field label={t.realCommTR} sub={t.realCommTRSub}>
              <input type="number" min="0" step="0.01" value={f.realCommTR === "" || f.realCommTR == null ? "" : f.realCommTR} onChange={function (e) { set("realCommTR", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, background: (f.realCommTR !== "" && f.realCommTR != null) ? "rgba(16,185,129,0.06)" : inp.background }} />
            </Field>
            <Field label={t.realDifferential} sub={t.realDifferentialSub}>
              <input type="number" min="0" step="0.01" value={f.realDifferential || ""} onChange={function (e) { set("realDifferential", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, background: f.realDifferential ? "rgba(16,185,129,0.06)" : inp.background }} />
            </Field>
            <Field label={t.realCommGFI} sub={t.realCommGFISub}>
              <input type="number" min="0" step="0.01" value={f.realCommGFI === "" || f.realCommGFI == null ? "" : f.realCommGFI} onChange={function (e) { set("realCommGFI", e.target.value); }} disabled={locked} style={{ ...inp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, background: (f.realCommGFI !== "" && f.realCommGFI != null) ? "rgba(16,185,129,0.06)" : inp.background }} />
            </Field>
            <Field label={t.returnRealGF}>
              <input value={c.hasReal ? fmtMoneyShort(c.realGF) : "—"} readOnly style={{ ...readOnlyInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
            <Field label={t.priceRealGrower}>
              <input value={c.hasReal ? fmtMoneyShort(c.realGrower) : "—"} readOnly style={{ ...readOnlyInp, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }} />
            </Field>
          </div>
          {c.hasReal && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#047857", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{t.realSummary}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                <ProfitCell title={t.colProfitDiff} amount={c.realProfitDiff} desc={t.profitDiffDesc} />
                <ProfitCell title={t.colProfitComm} amount={c.realProfitComm} desc={t.profitCommDesc} />
                <ProfitCell title={t.colProfitTotal} amount={c.realTotal} desc={t.profitTotalDesc} emphasis />
              </div>
            </div>
          )}
        </Section>

        <Section title={t.secMeta}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10 }}>
            <Field label={t.saleStatus}>
              <SearchSelect value={f.saleStatus || "pending"} onChange={function (v) { set("saleStatus", v); }} placeholder={t.selectStatus} options={statusOpts} disabled={locked} />
            </Field>
            <Field label={t.notes}>
              <textarea value={f.notes || ""} onChange={function (e) { set("notes", e.target.value); }} disabled={locked} placeholder={t.notesPlaceholder} rows={2} style={{ ...inp, resize: "vertical" }} />
            </Field>
          </div>
        </Section>
      </div>
    </DraggableModal>
  );
}

/* ======================== MAIN ======================== */
export default function ConsignmentPricingRegistry({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  var [rows, setRows] = useState([]);
  var [editing, setEditing] = useState(null);
  var [showForm, setShowForm] = useState(false);
  var [toast, setToast] = useState("");
  var [search, setSearch] = useState("");
  var [filterClient, setFilterClient] = useState("");
  var [filterStatus, setFilterStatus] = useState("");
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState("");
  var [dateTo, setDateTo] = useState("");
  var [cardFilter, setCardFilter] = useState("");
  var [selected, setSelected] = useState({});
  var [sortCol, setSortCol] = useState("date");
  var [sortDir, setSortDir] = useState("desc");
  var seasons = useMemo(function () { return loadSeasons(); }, []);

  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var aliveRef = useRef(true);
  var rowsRef = useRef([]);
  useEffect(function () { rowsRef.current = rows; }, [rows]);

  /* Source of truth = Supabase `consignment_pos`. Dual-write to
     localStorage `gfi-consignment-pos` for legacy sync consumers. */
  var writeRowsCache = function (next) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
  };
  var reload = async function () {
    setError(null);
    try {
      var data = await loadFromSupabase("consignment_pos", { force: true });
      if (aliveRef.current) { setRows(data); writeRowsCache(data); }
    } catch (e) {
      if (aliveRef.current) setError("Could not load POs: " + (e.message || String(e)));
    }
  };
  useEffect(function () {
    aliveRef.current = true;
    setError(null);
    loadFromSupabase("consignment_pos")
      .then(function (data) { if (aliveRef.current) { setRows(data); writeRowsCache(data); } })
      .catch(function (e) { if (aliveRef.current) setError("Could not load POs: " + (e.message || String(e))); });
    return function () { aliveRef.current = false; };
  }, []);

  var persistSingle = useCallback(async function (rec) {
    await saveToSupabase("consignment_pos", rec);
    var cur = rowsRef.current || [];
    var idx = cur.findIndex(function (r) { return r.id === rec.id; });
    var next = idx >= 0 ? cur.map(function (r, i) { return i === idx ? rec : r; }) : [rec].concat(cur);
    setRows(next); writeRowsCache(next);
  }, []);
  var persistDelete = useCallback(async function (id) {
    await deleteFromSupabase("consignment_pos", id);
    var next = (rowsRef.current || []).filter(function (r) { return r.id !== id; });
    setRows(next); writeRowsCache(next);
  }, []);

  /* Sync sale status from linked invoice when present. */
  var invoicesById = useMemo(function () {
    var out = {};
    getInvoices().forEach(function (inv) { if (inv.id != null) out[String(inv.id)] = inv; });
    return out;
  }, [rows]);
  var enriched = useMemo(function () {
    return rows.map(function (r) {
      if (!r.linkedInvoiceId) return r;
      var inv = invoicesById[String(r.linkedInvoiceId)];
      if (!inv) return r;
      /* PU# mirrors live from the linked invoice — it's owned by
         the invoice, not the PO. saleStatus is NOT mirrored: the
         PO's sale-cycle (pending → invoiced → settled / protection
         / etc.) is orthogonal to the invoice's lifecycle (draft /
         sent / paid). Mirroring would silently clobber a manual
         pick on every render. */
      var invPu = inv.puNumber != null && inv.puNumber !== "" ? inv.puNumber : r.puNumber;
      return { ...r, puNumber: invPu };
    });
  }, [rows, invoicesById]);

  /* Window globals for invoice integration. Mirrors the Fixed
     Pricing tab's pattern — invoice form can suggest a matching
     PO and push the real TR price back once known. */
  useEffect(function () {
    window.__gfiGetMatchingConsignmentPOs = function (dateStr, clientId) {
      if (!dateStr) return [];
      var target = new Date(dateStr + "T00:00:00").getTime();
      if (isNaN(target)) return [];
      var sevenDays = 7 * 24 * 3600 * 1000;
      var src = rowsRef.current || [];
      return src.filter(function (r) {
        if (clientId && String(r.clientId) !== String(clientId)) return false;
        if (!r.date) return false;
        var d = new Date(r.date + "T00:00:00").getTime();
        if (isNaN(d)) return false;
        return Math.abs(d - target) <= sevenDays;
      }).slice(0, 5);
    };
    window.__gfiLinkInvoiceToConsignmentPo = async function (poLineId, invoiceId, priceRealTR) {
      var current = rowsRef.current || [];
      var match = current.find(function (r) { return String(r.id) === String(poLineId); });
      if (!match) return;
      var updated = { ...match, linkedInvoiceId: invoiceId || "", priceRealTR: (priceRealTR != null && priceRealTR !== "") ? String(priceRealTR) : match.priceRealTR, updatedAt: new Date().toISOString() };
      try { await persistSingle(updated); } catch (e) { console.error("[ConsignmentPricing] link invoice failed", e); }
    };
    return function () {
      try { delete window.__gfiGetMatchingConsignmentPOs; delete window.__gfiLinkInvoiceToConsignmentPo; } catch (e) {}
    };
  }, [persistSingle]);

  var handleSave = async function (rec) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistSingle(rec);
      setToast(t.saved);
      setEditing(rec);
    } catch (e) {
      setError("Could not save PO line: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };
  var handleDelete = async function (id) {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      await persistDelete(id);
      setToast(t.deleted);
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      setError("Could not delete PO line: " + (e.message || String(e)));
      setToast("⚠ " + (e.message || String(e)));
    } finally { setSaving(false); }
  };

  var clientOpts = useMemo(function () {
    var uniq = [...new Set(rows.map(function (r) { return r.clientName; }).filter(Boolean))].sort();
    return uniq.map(function (n) { return { value: n, label: n }; });
  }, [rows]);

  var seasonFilteredRows = useMemo(function () {
    if (filterSeasons.length === 0) return enriched;
    return enriched.filter(function (r) {
      var sid = resolveSeasonId(seasons, r.date, r.seasonId);
      return sid && filterSeasons.indexOf(String(sid)) >= 0;
    });
  }, [enriched, filterSeasons, seasons]);

  var filtered = useMemo(function () {
    var list = seasonFilteredRows.slice();
    if (search) {
      var q = search.toLowerCase();
      list = list.filter(function (r) {
        return (r.poNumber || "").toLowerCase().includes(q)
          || (r.puNumber || "").toLowerCase().includes(q)
          || (r.productName || "").toLowerCase().includes(q)
          || (r.productBase || "").toLowerCase().includes(q)
          || (r.clientName || "").toLowerCase().includes(q);
      });
    }
    if (filterClient) list = list.filter(function (r) { return r.clientName === filterClient; });
    if (filterStatus) list = list.filter(function (r) { return (r.saleStatus || "pending") === filterStatus; });
    if (dateFrom) list = list.filter(function (r) { return r.date && r.date >= dateFrom; });
    if (dateTo)   list = list.filter(function (r) { return r.date && r.date <= dateTo; });
    if (cardFilter === "real")    list = list.filter(function (r) { return r.priceRealTR; });
    if (cardFilter === "settled") list = list.filter(function (r) { return r.saleStatus === "settled"; });
    return list;
  }, [seasonFilteredRows, search, filterClient, filterStatus, dateFrom, dateTo, cardFilter]);

  var sorted = useMemo(function () {
    var list = filtered.slice();
    list.sort(function (a, b) {
      var va, vb;
      var ca = calcConsignment(a), cb = calcConsignment(b);
      switch (sortCol) {
        case "date":         va = a.date || "";         vb = b.date || ""; break;
        case "delivery":     va = a.deliveryDate || ""; vb = b.deliveryDate || ""; break;
        case "week":         va = parseInt(a.week, 10) || 0; vb = parseInt(b.week, 10) || 0; break;
        case "client":       va = (a.clientName || "").toLowerCase(); vb = (b.clientName || "").toLowerCase(); break;
        case "po":           va = a.poNumber || "";     vb = b.poNumber || ""; break;
        case "product":      va = (a.productBase || "").toLowerCase(); vb = (b.productBase || "").toLowerCase(); break;
        case "cases":        va = parseInt(a.cases, 10) || 0; vb = parseInt(b.cases, 10) || 0; break;
        case "estProfit":    va = ca.estTotal; vb = cb.estTotal; break;
        case "realProfit":   va = ca.realTotal; vb = cb.realTotal; break;
        case "status":       va = a.saleStatus || ""; vb = b.saleStatus || ""; break;
        default: va = ""; vb = "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortCol, sortDir]);

  var toggleSort = function (col) {
    if (sortCol === col) { setSortDir(sortDir === "asc" ? "desc" : "asc"); return; }
    setSortCol(col); setSortDir("desc");
  };
  var arrow = function (col) {
    if (sortCol !== col) return <span style={{ marginLeft: 3, color: "var(--color-text-muted)", opacity: 0.4, fontSize: 9 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: "var(--color-accent)", fontSize: 9 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  /* Summary card totals over seasonFilteredRows (not `filtered`) so
     clicking the real/settled cards doesn't collapse the OTHER
     card's count against itself — same isolation pattern as
     BillRegistry/InvoiceRegistry/FixedPricingRegistry. Revenue uses
     TR midpoint (the working projection) until Real comes in. */
  var stats = useMemo(function () {
    var lines = seasonFilteredRows.length;
    var cases = 0, estRevenue = 0, estProfit = 0, realProfit = 0;
    var settled = 0;
    seasonFilteredRows.forEach(function (r) {
      var c = parseInt(r.cases, 10) || 0;
      var calc = calcConsignment(r);
      cases += c;
      estRevenue += calc.midTR * c;
      estProfit += calc.estTotal;
      if (calc.hasReal) realProfit += calc.realTotal;
      if (r.saleStatus === "settled") settled++;
    });
    return { lines: lines, cases: cases, estRevenue: estRevenue, estProfit: estProfit, realProfit: realProfit, settled: settled };
  }, [seasonFilteredRows]);

  /* Aggregator for both the selection bar and the module-total
     block. Revenue uses Real TR × cases when a real price is
     stamped (post-sale), otherwise the midpoint of the TR band so
     pre-sale rows still surface a working projection. Diff Profit
     is captured-per-line. Comm Profit and Est. / Real Profit follow
     calcConsignment's contract. */
  var aggregateRows = useCallback(function (list) {
    var out = { count: 0, cases: 0, revenue: 0, profitDiff: 0, profitComm: 0, estProfit: 0, realProfit: 0 };
    (list || []).forEach(function (r) {
      var c = parseInt(r.cases, 10) || 0;
      var calc = calcConsignment(r);
      var trForRevenue = calc.hasReal ? calc.realTR : calc.midTR;
      out.count       += 1;
      out.cases       += c;
      out.revenue     += trForRevenue * c;
      out.profitDiff  += calc.hasReal ? calc.realProfitDiff : calc.profitDiff;
      out.profitComm  += calc.hasReal ? calc.realProfitComm : calc.estProfitComm;
      out.estProfit   += calc.estTotal;
      if (calc.hasReal) out.realProfit += calc.realTotal;
    });
    return out;
  }, []);

  var selectedRows = useMemo(function () { return filtered.filter(function (r) { return selected[r.id]; }); }, [filtered, selected]);
  /* Export rows — selected if any checked, else full filtered set.
     Captures both the captured TR range and the derived GF / Grower
     numbers so the spreadsheet is self-contained for offline analysis. */
  var buildExportRows = function () {
    var src = selectedRows.length > 0 ? selectedRows : filtered;
    return src.map(function (r) {
      var calc = calcConsignment(r);
      return {
        "Loading/Delivery Date": xs(r.deliveryDate),
        "Departure Date": xs(r.date),
        "Week": xs(r.week ? "W" + r.week : ""),
        "Client": xs(r.clientName),
        "PO #": xs(r.poNumber),
        "PU #": xs(r.puNumber),
        "Product": xs(r.productDescription || r.productBase),
        "Category": xs(r.category),
        "Pallets": intOr(r.pallets),
        "Cases": intOr(r.cases),
        "Min TR": num2(r.minTR),
        "Max TR": num2(r.maxTR),
        "Comm TR %": num2(r.commTR),
        "Min GF": num2(calc.minGF),
        "Max GF": num2(calc.maxGF),
        "Differential": num2(r.differential),
        "Min Grower": num2(calc.minGrower),
        "Max Grower": num2(calc.maxGrower),
        "Comm GFI %": num2(r.commGFI),
        "Real TR": calc.hasReal ? num2(calc.realTR) : "",
        "Real Comm TR %": calc.hasRealCommTR ? num2(calc.realCommTR) : "",
        "Real Differential": calc.hasRealDiff ? num2(calc.realDiff) : "",
        "Real GF": calc.hasReal ? num2(calc.realGF) : "",
        "Real Grower": calc.hasReal ? num2(calc.realGrower) : "",
        "Real Comm GFI %": calc.hasRealCommGFI ? num2(calc.realCommGFI) : "",
        "Diff Profit": num2(calc.hasReal ? calc.realProfitDiff : calc.profitDiff),
        "Comm Profit": num2(calc.hasReal ? calc.realProfitComm : calc.estProfitComm),
        "Est. Profit": num2(calc.estTotal),
        "Real Profit": calc.hasReal ? num2(calc.realTotal) : "",
        "Sale Status": xs(r.saleStatus),
      };
    });
  };
  var selectedTotals = useMemo(function () { return aggregateRows(selectedRows); }, [selectedRows, aggregateRows]);
  var grandTotals = useMemo(function () { return aggregateRows(filtered); }, [filtered, aggregateRows]);
  var clearSelection = function () { setSelected({}); };
  var moneyCell = function (n, opts) {
    var v = parseFloat(n) || 0;
    var neg = v < 0;
    var color = neg ? "#DC2626" : (v > 0 ? (opts && opts.emphasize ? "var(--color-accent)" : "#047857") : "var(--color-text-muted)");
    var txt = neg ? "(" + fmtMoney(Math.abs(v)) + ")" : fmtMoney(v);
    return <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: color }}>{txt}</span>;
  };
  var selectedCount = selectedRows.length;

  /* Compact mode — headers are pre-line so `\n` in the label
     strings renders as a real 2-line wrap, keeping each column
     narrow. Padding / font-size tightened to fit the full table on
     screen without horizontal scroll. */
  var th = { padding: "5px 4px", textAlign: "left", fontWeight: 700, fontSize: 9, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", background: "var(--color-bg)", position: "sticky", top: 0, zIndex: 1, cursor: "pointer", whiteSpace: "pre-line", lineHeight: 1.15, verticalAlign: "bottom", borderBottom: "2px solid var(--color-border)" };
  var thR = { ...th, textAlign: "right" };
  var thC = { ...th, textAlign: "center" };
  var td = { padding: "5px 4px", fontSize: 10, borderBottom: "1px solid var(--color-border)", whiteSpace: "nowrap" };
  var tdR = { ...td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" };
  var tdC = { ...td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" };
  var tdDerived = { ...tdR, color: "var(--color-text-muted)", fontStyle: "italic" };

  var renderCard = function (key, label, value, color) {
    var active = cardFilter === key;
    return (
      <div key={key} onClick={function () { setCardFilter(active ? "" : key); }} style={{ padding: 12, borderRadius: 8, border: "1px solid " + (active ? color : "var(--color-border)"), background: active ? "rgba(42,122,111,0.05)" : "var(--color-bg-card)", cursor: "pointer", transition: "all 0.15s" }}>
        <div style={{ fontSize: 10, color: color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: color, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <ExportButton lang={lang} filenameBase="Consignment_Pricing_POs" selectedCount={selectedRows.length} totalCount={filtered.length} buildSummary={buildExportRows} buildDetail={buildExportRows} />
          <button onClick={function () { setEditing(null); setShowForm(true); }} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{t.add}</button>
        </div>
      </div>
      {error && (
        <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{error}</span>
          <button onClick={function() { setError(null); reload(); }} style={{ padding: "4px 10px", borderRadius: 4, border: "1px solid #991B1B", background: "transparent", color: "#991B1B", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Reintentar" : "Retry"}</button>
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 14 }}>
        {renderCard("lines",      t.cardLines,      stats.lines,                            "var(--color-accent)")}
        {renderCard("cases",      t.cardCases,      stats.cases.toLocaleString(),                 "#2563EB")}
        {renderCard("estRevenue", t.cardEstRevenue, fmtMoney(stats.estRevenue),             "#0F766E")}
        {renderCard("estProfit",  t.cardEstProfit,  fmtMoney(stats.estProfit),              "#B45309")}
        {renderCard("real",       t.cardRealProfit, fmtMoney(stats.realProfit),             "#047857")}
        {renderCard("settled",    t.cardSettled,    stats.settled,                          "#15803D")}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 280, flex: "1 1 200px" }} />
        <div style={{ minWidth: 170 }}><SearchSelect value={filterClient} onChange={setFilterClient} placeholder={t.allClients} options={clientOpts} /></div>
        <div style={{ minWidth: 170 }}><SearchSelect value={filterStatus} onChange={setFilterStatus} placeholder={t.allStatuses} options={SALE_STATUSES.map(function (s) { return { value: s, label: t["status" + s.charAt(0).toUpperCase() + s.slice(1)] }; })} /></div>
        <div style={{ minWidth: 180 }}><SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} /></div>
        <input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} style={{ ...inp, maxWidth: 140 }} title={lang === "es" ? "Desde" : "From"} />
        <input type="date" value={dateTo}   onChange={function (e) { setDateTo(e.target.value); }} style={{ ...inp, maxWidth: 140 }} title={lang === "es" ? "Hasta" : "To"} />
        {(dateFrom || dateTo || filterClient || filterStatus || cardFilter || search) && (
          <button onClick={function () { setSearch(""); setFilterClient(""); setFilterStatus(""); setDateFrom(""); setDateTo(""); setCardFilter(""); }} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        )}
      </div>

      {/* Selection summary bar — shown only when ≥1 row checked.
          Same shape as the bar in Fixed Pricing + BillRegistry. */}
      {selectedCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 10, fontSize: 12 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={async function () {
              if (saving) return;
              var msg = lang === "es" ? "¿Eliminar " + selectedCount + " línea(s) seleccionada(s)?" : "Delete " + selectedCount + " selected line(s)?";
              if (!window.confirm(msg)) return;
              setSaving(true); setError(null);
              try {
                var toDelete = rows.filter(function (r) { return selected[r.id]; });
                for (var i = 0; i < toDelete.length; i++) { await persistDelete(toDelete[i].id); }
                setSelected({});
                setToast(t.deleted);
              } catch (e) {
                setError("Could not delete: " + (e.message || String(e)));
                setToast("⚠ " + (e.message || String(e)));
              } finally { setSaving(false); }
            }} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{"🗑 "}{lang === "es" ? "Eliminar Seleccionados" : "Delete Selected"}</button>
            <button onClick={clearSelection} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
          </div>
          <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 14, flexWrap: "wrap", fontSize: 11 }}>
            <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Venta" : "Revenue"}: </span>{moneyCell(selectedTotals.revenue)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Cajas" : "Cases"}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString()}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Difer." : "Diff Profit"}: </span>{moneyCell(selectedTotals.profitDiff)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Com." : "Comm Profit"}: </span>{moneyCell(selectedTotals.profitComm)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Est." : "Est. Profit"}: </span>{moneyCell(selectedTotals.estProfit, { emphasize: true })}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{lang === "es" ? "Gan. Real" : "Real Profit"}: </span>{moneyCell(selectedTotals.realProfit, { emphasize: true })}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, minWidth: 240, paddingLeft: 14, borderLeft: "1px dashed var(--color-border)" }}>
            <div style={{ fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Total módulo" : "Module total"}</div>
            <div style={{ display: "flex", gap: 8, fontSize: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <span title={lang === "es" ? "Venta" : "Revenue"}>{moneyCell(grandTotals.revenue)}</span>
              <span title={lang === "es" ? "Cajas" : "Cases"} style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{grandTotals.cases.toLocaleString()}c</span>
              <span title={lang === "es" ? "Gan. Difer." : "Diff Profit"}>{moneyCell(grandTotals.profitDiff)}</span>
              <span title={lang === "es" ? "Gan. Com." : "Comm Profit"}>{moneyCell(grandTotals.profitComm)}</span>
              <span title={lang === "es" ? "Gan. Est." : "Est. Profit"}>{moneyCell(grandTotals.estProfit, { emphasize: true })}</span>
              <span title={lang === "es" ? "Gan. Real" : "Real Profit"}>{moneyCell(grandTotals.realProfit, { emphasize: true })}</span>
            </div>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14, border: "1px solid var(--color-border)", borderRadius: 10 }}>{t.noResults}</div>
      ) : (
        <div style={{ overflowX: "auto", maxHeight: "calc(100vh - 360px)", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...thC, width: 32 }}>
                  <input type="checkbox" checked={sorted.length > 0 && sorted.every(function (r) { return selected[r.id]; })} onChange={function (e) {
                    var next = { ...selected };
                    if (e.target.checked) sorted.forEach(function (r) { next[r.id] = true; });
                    else sorted.forEach(function (r) { delete next[r.id]; });
                    setSelected(next);
                  }} />
                </th>
                {/* Date columns per spec: Loading/Delivery, Departure, Week. */}
                <th onClick={function () { toggleSort("delivery"); }} style={th}>{t.colDelivery}{arrow("delivery")}</th>
                <th onClick={function () { toggleSort("date"); }} style={th}>{t.colDate}{arrow("date")}</th>
                <th onClick={function () { toggleSort("week"); }} style={thC}>{t.colWeek}{arrow("week")}</th>
                <th onClick={function () { toggleSort("client"); }} style={th}>{t.colClient}{arrow("client")}</th>
                <th onClick={function () { toggleSort("po"); }} style={th}>{t.colPO}{arrow("po")}</th>
                <th style={th}>{t.colPU}</th>
                <th onClick={function () { toggleSort("product"); }} style={th}>{t.colProduct}{arrow("product")}</th>
                <th style={thC}>{t.colCat}</th>
                <th style={thR}>{t.colPallets}</th>
                <th onClick={function () { toggleSort("cases"); }} style={thR}>{t.colCases}{arrow("cases")}</th>
                <th style={thR}>{t.colMinTR}</th>
                <th style={thR}>{t.colMaxTR}</th>
                <th style={thR}>{t.colCommTR}</th>
                <th style={thR}>{t.colMinGF}</th>
                <th style={thR}>{t.colMaxGF}</th>
                <th style={thR}>{t.colDifferential}</th>
                <th style={thR}>{t.colMinGrower}</th>
                <th style={thR}>{t.colMaxGrower}</th>
                <th style={thR}>{t.colCommGFI}</th>
                <th style={thR}>{t.colPriceRealTR}</th>
                <th style={thR}>{t.colRealCommTR}</th>
                <th style={thR}>{t.colRealDifferential}</th>
                <th style={thR}>{t.colReturnRealGF}</th>
                <th style={thR}>{t.colPriceRealGrower}</th>
                <th style={thR}>{t.colRealCommGFI}</th>
                <th style={thR}>{t.colProfitDiff}</th>
                <th style={thR}>{t.colProfitComm}</th>
                <th onClick={function () { toggleSort("estProfit"); }} style={thR}>{t.colEstTotal}{arrow("estProfit")}</th>
                <th onClick={function () { toggleSort("realProfit"); }} style={thR}>{t.colProfitTotal}{arrow("realProfit")}</th>
                <th onClick={function () { toggleSort("status"); }} style={thC}>{t.colSaleStatus}{arrow("status")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(function (r) {
                var c = calcConsignment(r);
                var openRow = function () { setEditing(r); setShowForm(true); };
                return (
                  <tr key={r.id} onClick={openRow} style={{ cursor: "pointer", background: selected[r.id] ? "rgba(42,122,111,0.05)" : "transparent" }}
                      onMouseEnter={function (e) { if (!selected[r.id]) e.currentTarget.style.background = "var(--color-bg)"; }}
                      onMouseLeave={function (e) { if (!selected[r.id]) e.currentTarget.style.background = "transparent"; }}>
                    <td style={{ ...tdC, width: 32 }} onClick={function (e) { e.stopPropagation(); }}>
                      <input type="checkbox" checked={!!selected[r.id]} onChange={function (e) { var next = { ...selected }; if (e.target.checked) next[r.id] = true; else delete next[r.id]; setSelected(next); }} />
                    </td>
                    {/* Loading/Delivery first, Departure second, Week. */}
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.deliveryDate || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.date || "—"}</td>
                    <td style={tdC}>{r.week ? "W" + r.week : "—"}</td>
                    <td style={td}>{r.clientName || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{r.poNumber || "—"}</td>
                    <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.puNumber || "—"}</td>
                    <td style={td}>{r.productDescription || r.productBase || "—"}</td>
                    <td style={tdC}>{r.category || "—"}</td>
                    <td style={tdR}>{r.pallets || "—"}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{r.cases || "—"}</td>
                    <td style={tdR}>{fmtMoneyShort(r.minTR)}</td>
                    <td style={tdR}>{fmtMoneyShort(r.maxTR)}</td>
                    <td style={tdR}>{r.commTR != null && r.commTR !== "" ? r.commTR + "%" : "—"}</td>
                    <td style={tdDerived}>{c.minGF > 0 ? fmtMoneyShort(c.minGF) : "—"}</td>
                    <td style={tdDerived}>{c.maxGF > 0 ? fmtMoneyShort(c.maxGF) : "—"}</td>
                    <td style={tdR}>{fmtMoneyShort(r.differential)}</td>
                    <td style={tdDerived}>{c.minGrower !== 0 || c.minGF > 0 ? fmtMoneyShort(c.minGrower) : "—"}</td>
                    <td style={tdDerived}>{c.maxGrower !== 0 || c.maxGF > 0 ? fmtMoneyShort(c.maxGrower) : "—"}</td>
                    <td style={tdR}>{r.commGFI != null && r.commGFI !== "" ? r.commGFI + "%" : "—"}</td>
                    <td style={{ ...tdR, color: c.hasReal ? "#047857" : "var(--color-text-muted)", fontWeight: c.hasReal ? 700 : 400 }}>{c.hasReal ? fmtMoneyShort(c.realTR) : "—"}</td>
                    <td style={{ ...tdR, color: c.hasRealCommTR ? "#047857" : "var(--color-text-muted)", fontWeight: c.hasRealCommTR ? 700 : 400 }}>{c.hasRealCommTR ? (c.realCommTR + "%") : "—"}</td>
                    <td style={{ ...tdR, color: c.hasRealDiff ? "#047857" : "var(--color-text-muted)", fontWeight: c.hasRealDiff ? 700 : 400 }}>{c.hasRealDiff ? fmtMoneyShort(c.realDiff) : "—"}</td>
                    <td style={tdDerived}>{c.hasReal ? fmtMoneyShort(c.realGF) : "—"}</td>
                    <td style={tdDerived}>{c.hasReal ? fmtMoneyShort(c.realGrower) : "—"}</td>
                    <td style={{ ...tdR, color: c.hasRealCommGFI ? "#047857" : "var(--color-text-muted)", fontWeight: c.hasRealCommGFI ? 700 : 400 }}>{c.hasRealCommGFI ? (c.realCommGFI + "%") : "—"}</td>
                    <td style={{ ...tdR, color: (c.hasReal ? c.realProfitDiff : c.profitDiff) >= 0 ? "#047857" : "#B91C1C" }}>{fmtMoneyShort(c.hasReal ? c.realProfitDiff : c.profitDiff)}</td>
                    <td style={{ ...tdR, color: (c.hasReal ? c.realProfitComm : c.estProfitComm) >= 0 ? "#047857" : "#B91C1C" }}>{fmtMoneyShort(c.hasReal ? c.realProfitComm : c.estProfitComm)}</td>
                    <td style={{ ...tdR, fontWeight: 700, color: c.estTotal >= 0 ? "var(--color-accent)" : "#B91C1C" }}>{fmtMoneyShort(c.estTotal)}</td>
                    <td style={{ ...tdR, fontWeight: 700, color: c.hasReal ? "#047857" : "var(--color-text-muted)" }}>{c.hasReal ? fmtMoneyShort(c.realTotal) : "—"}</td>
                    <td style={tdC}><StatusBadge status={r.saleStatus || "pending"} t={t} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <PoLineModal line={editing} t={t} lang={lang} onSave={handleSave} onDelete={handleDelete} onClose={function () { setShowForm(false); setEditing(null); }} />}
      <Toast msg={toast} onDismiss={function () { setToast(""); }} />
    </div>
  );
}
